import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './swatch.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'


export const swatchVariants = ['text', 'color', 'image'] as const
export type swatchVariantTypes = typeof swatchVariants

export const swatchSizes = ['small', 'large'] as const
export type swatchSizeTypes = typeof swatchSizes

@customElement('cse-swatch')
export class CSESwatch extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: String })
  variant: swatchVariantTypes[number] = 'text'

  @property({ type: String })
  size: swatchSizeTypes[number] = 'small'

  @property({ type: String })
  color = '#C1C1C1'

  @property({ type: String, attribute: 'image-source' })
  imageSource = ''

  @property({ type: String, attribute: 'image-alt' })
  imageAlt = ''

  @property({ type: String })
  tooltip = ''

  @property({ type: Boolean })
  selected = false

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean, attribute: 'out-of-stock' })
  outOfStock = false

  @property({ type: String })
  url = ''

  private renderContent = () => {
    switch(this.variant) {
      case 'text':
        return html`<slot></slot>`
      case 'image':
        return html`<cse-image source="${this.imageSource}" alt="${this.imageAlt}" fit="cover"></cse-image>`
      default:
        return nothing
    }
  }

  private renderColorStyle = () => this.variant === 'color'
    ? `background-color: ${this.color}`
    : ''

  private renderSwatchButton = (classes: string) => {
    if (this.url !== '') {
      return html`
        <a
          href="${this.url}"
          slot="${this.tooltip ? 'anchor' : ''}"
          class="${classes}"
          style="${this.renderColorStyle()}"
          tabindex="${this.disabled ? -1 : 0}"
          ?disabled=${this.disabled}
        >
          ${this.renderContent()}
        </a>
      `
    } else {
      return html`
        <button
          slot="${this.tooltip ? 'anchor' : ''}"
          class="${classes}"
          style="${this.renderColorStyle()}"
          tabindex="${this.disabled ? -1 : 0}"
          ?disabled=${this.disabled}
        >
          ${this.renderContent()}
        </button>
      `
    }
  }

  override render() {
    const classes = classMap({
      'cse-swatch': true,
      'font-body-s': true,
      [`variant-${this.variant}`]: true,
      [`size-${this.size}`]: true,
      'selected': this.selected,
      'disabled': this.disabled,
      'out-of-stock': this.outOfStock,
    })

    return this.tooltip ? html`
      <cse-tooltip>
        ${this.renderSwatchButton(classes as string)}
        ${this.tooltip}
      </cse-tooltip>
    ` : this.renderSwatchButton(classes as string)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-swatch': CSESwatch
  }
}
